/**
 * Plugin Loader
 *
 * Dynamically loads and registers UI plugins from workspace packages.
 * Plugins are loaded based on company configuration.
 */

/**
 * Plugin module interface
 */
type PluginModule = {
  register: () => void;
  unregister?: () => void;
  pluginId?: string;
};

/**
 * Loaded plugins map (pluginId -> module)
 */
const loadedPlugins = new Map<string, PluginModule>();

/**
 * Hard-disabled plugins: skipped even if a company has them in enabledPlugins.
 * Code stays in the workspace but is never registered, and the packages are
 * excluded from the build (their `build` scripts are no-ops). The matching
 * pluginLoaders entries are commented out so the bundler does not pull them in.
 *
 * `shipment-planner` stays here for a specific reason worth stating, because it
 * is easy to assume it belongs with the rest: there is no shipment-planner
 * BACKEND plugin at all, and its pages read from `data/planner-mock` — one of
 * them says so in its own header ("Presentation-only: orders come from
 * ../data/planner-mock, never from an API"). Switching it on would ship a
 * screen of invented numbers.
 *
 * `tracking` was removed from this set (QA ledger OT-001). It is the opposite
 * case: a complete board over a real read model — `GET /api/shipments/tracking`
 * answers with etaLive, etaBaseline, etaSource, trackingPhase, currentMilestone
 * and every tracking reference — with server-side sorting, mode/status filters
 * and column settings. It was dead code behind a flag, not an unfinished
 * feature. Removing it here does not turn anything on: a company still has to
 * carry `tracking` in its own enabledPlugins.
 */
const DISABLED_PLUGINS = new Set<string>(['rfq', 'finance', 'shipment-planner']);

/**
 * Plugin loaders - direct imports from workspace packages
 */
const pluginLoaders: Record<string, () => Promise<PluginModule>> = {
  // Core plugins (always available to certain roles)
  admin: () => import('@lognai/plugin-admin-ui/register'),
  // Same package, second entry point: the platform-only admin surfaces. Scoped
  // by its own id so the loader — not code inside the bundle — decides whether a
  // session gets them. See loadPluginsForUser.
  'admin-platform': () => import('@lognai/plugin-admin-ui/register-platform'),

  // Company plugins (loaded based on company configuration)
  products: () => import('@lognai/plugin-products-ui/register'),
  warehouse: () => import('@lognai/plugin-warehouse-ui/register'),
  orders: () => import('@lognai/plugin-orders-ui/register'),
  shipments: () => import('@lognai/plugin-shipments-ui/register'),
  dd: () => import('@lognai/plugin-dd-ui/register'),
  'temporary-permits': () => import('@lognai/plugin-temporary-permits-ui/register'),
  'vendor-onboarding': () => import('@lognai/plugin-vendor-onboarding-ui/register'),
  inventory: () => import('@lognai/plugin-inventory-ui/register'),
  'port-lookup': () => import('@lognai/plugin-port-lookup-ui/register'),
  analytics: () => import('@lognai/plugin-analytics-ui/register'),
  'ai-core': () => import('@lognai/plugin-ai-core-ui/register'),
  procurement: () => import('@lognai/plugin-procurement-ui/register'),
  'freight-rfq': () => import('@lognai/plugin-freight-rfq-ui/register'),
  'landed-cost': () => import('@lognai/plugin-landed-cost-ui/register'),
  'hs-classifier': () => import('@lognai/plugin-hs-classifier-ui/register'),
  'ocean-schedules': () => import('@lognai/plugin-ocean-schedules-ui/register'),
  'global-insights': () => import('@lognai/plugin-global-insights-ui/register'),
  tasks: () => import('@lognai/plugin-tasks-ui/register'),
  tracking: () => import('@lognai/plugin-tracking-ui/register'),
  'shipment-planner': () => import('@lognai/plugin-shipment-planner-ui/register'),
  'freight-audit': () => import('@lognai/plugin-freight-audit-ui/register'),
  fleetmon: () => import('@lognai/plugin-fleetmon-ui/register'),
  'master-data': () => import('@lognai/plugin-master-data-ui/register'),
  // Same package, second entry point — see 'admin-platform' above. Master Data
  // is company-enabled, and a platform admin has no company, so without this
  // the only role allowed to edit the Ports register never sees the section.
  'master-data-platform': () => import('@lognai/plugin-master-data-ui/register-platform'),
  workflows: () => import('@lognai/plugin-workflows-ui/register'),
  // Same package, second entry point — see 'master-data-platform' above. Forms
  // are company-enabled and a platform admin has no company, so the per-company
  // workflow admin (definitions, lists) loads under its own id.
  'workflows-platform': () => import('@lognai/plugin-workflows-ui/register-platform'),
  invoices: () => import('@lognai/plugin-invoices-ui/register'),
  // Disabled plugins (see DISABLED_PLUGINS) — kept commented so the bundler
  // does not resolve their workspace packages:
  // rfq: () => import('@lognai/plugin-rfq-ui/register'),
  // finance: () => import('@lognai/plugin-finance-ui/register'),
  // shipment-planner DOES have a workspace package and is listed above, but
  // stays in DISABLED_PLUGINS — see the note there; it has no backend.
  // freight-audit is NOT disabled here — it loads normally.
};

/**
 * Core plugins that are loaded based on role, not company config
 * Empty array means all authenticated users get this plugin
 */
const CORE_PLUGINS: Record<string, string[]> = {
  admin: [], // All authenticated users (company features merged into admin)
  tasks: [], // Task management available to all authenticated users
  // Presentation-only planning dashboard (static mock, no backend / permissions),
  // surfaced as a flat "Shipment Planner" nav item for all internal users.
  'shipment-planner': [],
};

/**
 * Determine which core plugins to load based on user roles
 *
 * @param userRoles - Array of user's roles
 */
function getCorePluginsForRoles(userRoles: string[]): string[] {
  const plugins: string[] = [];

  for (const [pluginId, requiredRoles] of Object.entries(CORE_PLUGINS)) {
    // Empty array means all authenticated users get this plugin
    if (requiredRoles.length === 0 || requiredRoles.some((role) => userRoles.includes(role))) {
      plugins.push(pluginId);
    }
  }

  return plugins;
}

/**
 * Load and register plugins based on enabled list
 *
 * @param enabledPlugins - Array of plugin IDs to load
 */
export async function loadPlugins(enabledPlugins: string[]): Promise<void> {
  const skipped = enabledPlugins.filter((id) => DISABLED_PLUGINS.has(id));
  if (skipped.length > 0) {
    console.warn('[PluginLoader] Skipping hard-disabled plugins:', skipped);
  }
  const pluginsToLoad = enabledPlugins.filter((id) => !DISABLED_PLUGINS.has(id));

  // Treat enabledPlugins as the complete entitlement set. This matters when
  // switching companies: a plugin loaded for the previous company must not
  // leave its routes or navigation visible for the new company.
  for (const pluginId of getLoadedPlugins()) {
    if (!pluginsToLoad.includes(pluginId)) unloadPlugin(pluginId);
  }

  console.log('[PluginLoader] Loading plugins:', pluginsToLoad);

  const loadPromises = pluginsToLoad.map(async (pluginId) => {
    if (loadedPlugins.has(pluginId)) {
      console.log(`[PluginLoader] Plugin already loaded: ${pluginId}`);
      return;
    }

    const loader = pluginLoaders[pluginId];
    if (!loader) {
      console.warn(`[PluginLoader] Unknown plugin: ${pluginId}`);
      return;
    }

    try {
      const plugin = await loader();
      plugin.register();
      loadedPlugins.set(pluginId, plugin);
      console.log(`[PluginLoader] Loaded plugin: ${pluginId}`);
    } catch (error) {
      console.error(`[PluginLoader] Failed to load plugin: ${pluginId}`, error);
    }
  });

  await Promise.all(loadPromises);
  console.log('[PluginLoader] All plugins loaded');
}

/**
 * Load plugins for a user based on their roles and company configuration
 *
 * @param userRoles - User's roles
 * @param companyEnabledPlugins - Plugins enabled for user's company
 */
export async function loadPluginsForUser(
  userRoles: string[],
  companyEnabledPlugins: string[],
  options: { isExternalPartner?: boolean; isPlatformAdmin?: boolean } = {}
): Promise<void> {
  // External partner/supplier users get a minimal portal: only their company's
  // enabled plugins (e.g. vendor-onboarding), not internal core plugins (admin).
  const corePlugins = options.isExternalPartner ? [] : getCorePluginsForRoles(userRoles);

  // The platform-only admin surfaces (tenant list, platform dashboard, global
  // permission catalogue) are a separate bundle, loaded only for a real platform
  // admin. Passed in rather than derived from `userRoles` here because the
  // canonical test also requires `companyId == null`, which this function does
  // not receive — see core-platform's `isPlatformAdmin`.
  const platformPlugins =
    !options.isExternalPartner && options.isPlatformAdmin
      ? ['admin-platform', 'master-data-platform', 'workflows-platform']
      : [];

  // Combine core plugins with company-enabled plugins
  const allPlugins = [...new Set([...corePlugins, ...platformPlugins, ...companyEnabledPlugins])];

  console.log('[PluginLoader] Loading plugins for user:', {
    roles: userRoles,
    corePlugins,
    companyPlugins: companyEnabledPlugins,
    allPlugins,
  });

  await loadPlugins(allPlugins);
}

/**
 * Unload a specific plugin
 *
 * @param pluginId - Plugin ID to unload
 */
export function unloadPlugin(pluginId: string): void {
  const plugin = loadedPlugins.get(pluginId);
  if (plugin) {
    if (plugin.unregister) {
      plugin.unregister();
    }
    loadedPlugins.delete(pluginId);
    console.log(`[PluginLoader] Unloaded plugin: ${pluginId}`);
  }
}

/**
 * Unload all plugins
 */
export function unloadAllPlugins(): void {
  for (const [pluginId, plugin] of loadedPlugins) {
    if (plugin.unregister) {
      plugin.unregister();
    }
    console.log(`[PluginLoader] Unloaded plugin: ${pluginId}`);
  }
  loadedPlugins.clear();
}

/**
 * Get list of loaded plugin IDs
 */
export function getLoadedPlugins(): string[] {
  return Array.from(loadedPlugins.keys());
}

/**
 * Check if a plugin is loaded
 */
export function isPluginLoaded(pluginId: string): boolean {
  return loadedPlugins.has(pluginId);
}

/**
 * Get all available plugin IDs
 */
export function getAvailablePlugins(): string[] {
  return Object.keys(pluginLoaders);
}
